import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Search, Filter, MoreVertical, HelpCircle, X, Archive, ArchiveRestore, Send } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { ProposalAllowance } from '../components/ProposalAllowance';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

// Kanban DnD — column title → target status key. `emailsCustomer=true`
// means dropping there will fire a customer email (needs confirmation).
const DND_TARGET_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  approved: 'Approved',
  in_production: 'In Production',
  ready_for_pickup: 'Ready for Pickup',
};
const DND_TARGETS = {
  Draft: { status: 'draft', emailsCustomer: false, hint: 'Move back to draft — nothing sent to the customer.' },
  Sent: { status: 'sent', emailsCustomer: false, hint: 'Mark as sent. Use the Send button on the proposal to actually email it.' },
  Approved: { status: 'approved', emailsCustomer: true, hint: 'Customer approved — thank-you email will be sent.' },
  'In Production': { status: 'in_production', emailsCustomer: true, hint: 'Notify the customer their order is being produced.' },
  'Ready for Pickup': { status: 'ready_for_pickup', emailsCustomer: true, hint: 'Notify the customer their order is ready to pick up.' },
  // Paid / Outstanding Balances / Archived are NOT drop targets — those states are computed from payments/flags.
};

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState(null); // 'unpaid' or null
  const [openMenuId, setOpenMenuId] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [emailsPaused, setEmailsPaused] = useState(false);
  // Drag-and-drop state
  const [draggingProposal, setDraggingProposal] = useState(null); // { id, fromStatus, customer_name, customer_email }
  const [dropTargetStatus, setDropTargetStatus] = useState(null);
  const [dndConfirm, setDndConfirm] = useState(null); // { proposal, targetStatus, targetLabel, emailsCustomer, hint }
  const [dndSaving, setDndSaving] = useState(false);

  // Status change over the wire — reused by DnD + the future dropdown flow.
  const applyStatusChangeFromDnD = async (proposalId, targetStatus, sendCustomerEmail) => {
    setDndSaving(true);
    // Optimistic UI — update local status immediately so the card visually snaps into the target column.
    const rollback = proposals.find((p) => p.id === proposalId)?.status;
    setProposals((prev) => prev.map((p) => (p.id === proposalId ? { ...p, status: targetStatus } : p)));
    try {
      const res = await axios.post(`/proposals/${proposalId}/status`, {
        status: targetStatus,
        send_customer_email: sendCustomerEmail,
      });
      // Merge server-of-truth back so recalculated fields are correct.
      setProposals((prev) => prev.map((p) => (p.id === proposalId ? res.data : p)));
      toast.success(
        [`Moved to ${DND_TARGET_LABELS[targetStatus] || targetStatus}`,
         res.data._order_created ? `Order ${res.data._order_po_number} created` : null,
         res.data._email_sent ? 'Email sent' : null].filter(Boolean).join(' · ')
      );
      setDndConfirm(null);
    } catch (e) {
      // Revert
      setProposals((prev) => prev.map((p) => (p.id === proposalId ? { ...p, status: rollback } : p)));
      toast.error(e.response?.data?.detail || 'Move failed');
    } finally {
      setDndSaving(false);
    }
  };

  // Fetch email-pause status once — shows a warning banner whenever outgoing
  // email is globally suppressed via the EMAILS_PAUSED env flag.
  useEffect(() => {
    axios.get('/admin/email-status').then((r) => setEmailsPaused(!!r.data?.paused)).catch(() => {});
  }, []);

  // Check for filter in URL on mount
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'unpaid') {
      setFilterMode('unpaid');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProposals();
  }, [activeTab]);

  const fetchProposals = async () => {
    setLoading(true);
    try {
      const url = activeTab === 'archived' ? '/proposals?archived_only=1' : '/proposals';
      const response = await axios.get(url);
      setProposals(response.data);
    } catch (error) {
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
    // Always refresh archived count for the tab badge
    try {
      const stats = await axios.get('/proposals/stats');
      setArchivedCount(stats.data.archived || 0);
    } catch { /* ignore */ }
  };

  const archiveProposal = async (id) => {
    try {
      await axios.post(`/proposals/${id}/archive`);
      toast.success('Proposal archived');
      setOpenMenuId(null);
      fetchProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    }
  };

  const unarchiveProposal = async (id) => {
    try {
      await axios.post(`/proposals/${id}/unarchive`);
      toast.success('Proposal restored');
      setOpenMenuId(null);
      fetchProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const [remindingId, setRemindingId] = useState(null);
  const sendBalanceReminder = async (proposal) => {
    if (remindingId) return;
    if (!window.confirm(
      `Email ${proposal.customer_name || 'the customer'} a reminder for the outstanding balance on ${proposal.proposal_number}?`
    )) return;
    setRemindingId(proposal.id);
    try {
      const { data } = await axios.post(`/proposals/${proposal.id}/send-balance-reminder`);
      if (data?.success) {
        toast.success(`Reminder sent — $${(data.balance_due || 0).toFixed(2)} balance`);
      } else {
        toast.warning('Send returned no success — check kill switch / SMTP credentials.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  // Group proposals by status for Kanban view
  const draftProposals = proposals.filter(p => p.status === 'draft');
  const sentProposals = proposals.filter(p => p.status === 'sent');
  const approvedProposals = proposals.filter(p => p.status === 'approved');
  const inProductionProposals = proposals.filter(p => p.status === 'in_production');
  const readyForPickupProposals = proposals.filter(p => p.status === 'ready_for_pickup' || p.status === 'shipped');
  const paidProposals = proposals.filter(
    p => p.status === 'paid' || p.status === 'ordered' || p.status === 'delivered' || p.status === 'picked_up' || p.status === 'completed'
  );
  // Outstanding-balance bucket: any proposal where customer paid SOMETHING
  // but still owes — includes status='partially_paid' AND any proposal whose
  // payments array sums under total_amount (catches stale data pre-reconcile).
  const outstandingProposals = proposals.filter(p => {
    const payments = p.payments || [];
    const paid = payments.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    const total = Number(p.total_amount) || 0;
    return paid > 0 && total - paid >= 0.01 && !p.archived;
  });

  // Calculate totals
  const calcTotal = (list) => list.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  const draftTotal = calcTotal(draftProposals);
  const sentTotal = calcTotal(sentProposals);
  const approvedTotal = calcTotal(approvedProposals);
  const inProductionTotal = calcTotal(inProductionProposals);
  const readyForPickupTotal = calcTotal(readyForPickupProposals);
  const paidTotal = calcTotal(paidProposals);
  // Sum of outstanding balances (NOT total amounts) — what's still owed.
  const outstandingTotal = outstandingProposals.reduce((sum, p) => {
    const payments = p.payments || [];
    const paid = payments.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    return sum + Math.max((Number(p.total_amount) || 0) - paid, 0);
  }, 0);
  const openTotal = draftTotal + sentTotal;

  // Filter by search
  const filterBySearch = (list) => {
    if (!searchTerm) return list;
    const search = searchTerm.toLowerCase();
    return list.filter(p => 
      (p.proposal_number || '').toLowerCase().includes(search) ||
      (p.customer_name || '').toLowerCase().includes(search)
    );
  };

  const getInitials = (name) => {
    if (!name) return 'NN';
    const parts = name.split(' ');
    return parts.map(p => p.charAt(0).toUpperCase()).join('').slice(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Compute outstanding balance from the payments array so we can flag
  // "paid-but-edited" proposals where amount_paid < total_amount.
  const getBalanceDue = (proposal) => {
    const payments = proposal.payments || [];
    const paid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const total = Number(proposal.total_amount) || 0;
    return { paid, balance: Math.max(total - paid, 0) };
  };

  const ProposalCard = ({ proposal }) => {
    const { paid, balance } = getBalanceDue(proposal);
    const hasOutstandingBalance = paid > 0 && balance >= 0.01;
    const isDragging = draggingProposal?.id === proposal.id;
    return (
    <div 
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', proposal.id);
        setDraggingProposal({
          id: proposal.id,
          fromStatus: proposal.status,
          customer_name: proposal.customer_name,
          customer_email: proposal.customer_email,
        });
      }}
      onDragEnd={() => { setDraggingProposal(null); setDropTargetStatus(null); }}
      style={{
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px',
        boxShadow: isDragging ? '0 8px 24px rgba(15,23,42,0.18)' : '0 1px 3px rgba(0,0,0,0.08)',
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? 'rotate(-1deg)' : 'none',
        transition: 'box-shadow 0.2s, opacity 0.15s, transform 0.15s',
        borderLeft: '3px solid #e74c3c'
      }}
      onClick={() => navigate(`/proposal/${proposal.id}`)}
      onMouseEnter={(e) => { if (!isDragging) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
      onMouseLeave={(e) => { if (!isDragging) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; }}
      data-testid={`proposal-card-${proposal.id}`}
    >
      {(proposal.art_approved || proposal.print_date) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {proposal.art_approved && (
            <span data-testid={`art-approved-badge-${proposal.id}`} style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: 999, letterSpacing: '0.03em' }}>
              ✓ ART APPROVED
            </span>
          )}
          {proposal.print_date && (
            <span data-testid={`print-date-badge-${proposal.id}`} style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 999, letterSpacing: '0.03em' }}>
              PRINT {new Date(proposal.print_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Avatar */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: '#e74c3c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: '600',
          fontSize: '14px',
          flexShrink: 0
        }}>
          {getInitials(proposal.customer_name)}
        </div>
        
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ 
            fontSize: '13px', 
            color: '#e74c3c', 
            margin: '0 0 4px 0',
            fontWeight: '600'
          }}>
            {proposal.proposal_number || `P-${proposal.id?.slice(0, 6)}`}
          </p>
          <h4 style={{ 
            fontSize: '15px', 
            fontWeight: '700', 
            margin: '0 0 4px 0',
            color: '#111827',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {proposal.customer_name || 'No Name'}
          </h4>
          <p style={{ 
            fontSize: '12px', 
            color: '#6b7280', 
            margin: 0 
          }}>
            {user?.company?.name || brandName()}
          </p>
        </div>
        
        {/* Amount & Menu */}
        <div style={{ textAlign: 'right', position: 'relative' }}>
          <p style={{ 
            fontSize: '15px', 
            fontWeight: '700', 
            color: '#111827',
            margin: '0 0 4px 0'
          }}>
            {formatCurrency(proposal.total_amount)}
          </p>
          {hasOutstandingBalance && (
            <div
              data-testid={`balance-badge-${proposal.id}`}
              title={`Paid ${formatCurrency(paid)} of ${formatCurrency(proposal.total_amount)} — ${formatCurrency(balance)} outstanding`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px',
                borderRadius: 999,
                background: '#fff7ed',
                border: '1px solid #fdba74',
                color: '#c2410c',
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 4,
                whiteSpace: 'nowrap',
              }}
            >
              ⚠ {formatCurrency(balance)} due
            </div>
          )}
          <button 
            onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === proposal.id ? null : proposal.id); }}
            data-testid={`proposal-menu-${proposal.id}`}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              padding: '4px',
              color: '#9ca3af'
            }}
          >
            <MoreVertical size={16} />
          </button>
          {openMenuId === proposal.id && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', right: 0, top: 28, zIndex: 30,
                background: 'white', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                border: '1px solid #e5e7eb', minWidth: 180, overflow: 'hidden',
              }}
            >
              {proposal.archived ? (
                <button
                  onClick={() => unarchiveProposal(proposal.id)}
                  data-testid={`unarchive-btn-${proposal.id}`}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <ArchiveRestore size={14} /> Restore from archive
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (window.confirm(`Archive proposal ${proposal.proposal_number}? It will be hidden from the active list but preserved.`)) {
                      archiveProposal(proposal.id);
                    }
                  }}
                  data-testid={`archive-btn-${proposal.id}`}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <Archive size={14} /> Archive
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  };

  // Variant of ProposalCard for the Outstanding Balances column — shows
  // total/paid/balance breakdown plus an inline "Send Reminder" button.
  const BalanceCard = ({ proposal }) => {
    const { paid, balance } = getBalanceDue(proposal);
    const isReminding = remindingId === proposal.id;
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '14px',
          marginBottom: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          borderLeft: '3px solid #f59e0b',
        }}
        data-testid={`balance-card-${proposal.id}`}
      >
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/proposal/${proposal.id}`)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ fontSize: 13, color: '#c2410c', margin: 0, fontWeight: 700 }}>
                {proposal.proposal_number || `P-${proposal.id?.slice(0, 6)}`}
              </p>
              <h4
                style={{
                  fontSize: 14, fontWeight: 700, margin: '4px 0 0', color: '#111827',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {proposal.customer_name || 'No Name'}
              </h4>
            </div>
            <div
              style={{
                padding: '2px 8px', borderRadius: 999, background: '#fff7ed',
                border: '1px solid #fdba74', color: '#c2410c', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
              }}
            >
              ⚠ {formatCurrency(balance)}
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: '#475569', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <span>Order total</span>
            <span style={{ textAlign: 'right', color: '#0f172a', fontWeight: 600 }}>{formatCurrency(proposal.total_amount)}</span>
            <span>Paid</span>
            <span style={{ textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{formatCurrency(paid)}</span>
            <span style={{ color: '#c2410c', fontWeight: 700 }}>Balance due</span>
            <span style={{ textAlign: 'right', color: '#c2410c', fontWeight: 800 }}>{formatCurrency(balance)}</span>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); sendBalanceReminder(proposal); }}
          disabled={isReminding}
          data-testid={`send-reminder-${proposal.id}`}
          style={{
            marginTop: 12, width: '100%', padding: '8px 12px',
            background: isReminding ? '#fed7aa' : '#c2410c',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: isReminding ? 'wait' : 'pointer',
            fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Send size={14} />
          {isReminding ? 'Sending…' : 'Send Balance Reminder'}
        </button>
      </div>
    );
  };

  const KanbanColumn = ({ title, count, total, proposals, color, cardComponent: CardComp = ProposalCard, totalLabel }) => {
    const dropTarget = DND_TARGETS[title];
    const isDropCandidate = draggingProposal && dropTarget && dropTarget.status !== draggingProposal.fromStatus;
    const isActiveDrop = dropTargetStatus === (dropTarget?.status || null) && isDropCandidate;

    const handleDrop = (e) => {
      e.preventDefault();
      setDropTargetStatus(null);
      if (!dropTarget || !draggingProposal) return;
      if (dropTarget.status === draggingProposal.fromStatus) return; // no-op
      const proposalObj = { id: draggingProposal.id, customer_email: draggingProposal.customer_email, customer_name: draggingProposal.customer_name };
      if (dropTarget.emailsCustomer && draggingProposal.customer_email) {
        // Ask before firing the email
        setDndConfirm({
          proposal: proposalObj,
          targetStatus: dropTarget.status,
          targetLabel: title,
          emailsCustomer: true,
          hint: dropTarget.hint,
        });
      } else {
        applyStatusChangeFromDnD(proposalObj.id, dropTarget.status, false);
      }
    };

    return (
    <div
      data-testid={`kanban-column-${title.toLowerCase().replace(/\s+/g, '-')}`}
      onDragOver={(e) => {
        if (!isDropCandidate) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTargetStatus !== dropTarget.status) setDropTargetStatus(dropTarget.status);
      }}
      onDragLeave={(e) => {
        // only clear if we truly leave the column bounds
        if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetStatus(null);
      }}
      onDrop={handleDrop}
      style={{ 
        flex: 1, 
        minWidth: '300px',
        background: isActiveDrop ? '#eef2ff' : '#f9fafb',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 240px)',
        outline: isActiveDrop ? `2px dashed ${color}` : 'none',
        outlineOffset: '-4px',
        transition: 'background 0.15s, outline-color 0.15s',
      }}>
      {/* Column Header */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #e5e7eb',
        background: 'white',
        borderRadius: '8px 8px 0 0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h3 style={{ 
            fontSize: '14px', 
            fontWeight: '700', 
            color: '#374151',
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {title} <span style={{ color: '#9ca3af', fontWeight: '500' }}>({count})</span>
          </h3>
        </div>
        <p style={{ 
          fontSize: '18px', 
          fontWeight: '700', 
          color: color,
          margin: 0
        }}>
          {formatCurrency(total)}
        </p>
        {totalLabel && (
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {totalLabel}
          </p>
        )}
      </div>
      
      {/* Cards */}
      <div style={{ 
        padding: '12px', 
        overflowY: 'auto',
        flex: 1
      }}>
        {proposals.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '24px 0' }}>
            No proposals
          </p>
        ) : (
          proposals.map(p => <CardComp key={p.id} proposal={p} />)
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="dashboard" data-testid="dashboard">
      {/* Header */}
      <AppHeader />
      {emailsPaused && (
        <div
          data-testid="emails-paused-banner"
          style={{
            background: '#fef3c7',
            borderBottom: '1px solid #f59e0b',
            color: '#92400e',
            padding: '10px 16px',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <span role="img" aria-label="pause">⏸</span>
          OUTGOING EMAILS ARE PAUSED — no client emails, follow-ups, or notifications are being sent. Toggle via <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 4px', borderRadius: 4 }}>EMAILS_PAUSED</code> in backend/.env.
        </div>
      )}

      <main className="dashboard-content" style={{ padding: '24px' }}>
        {/* Filter Banner - shows when filtering by unpaid */}
        {filterMode === 'unpaid' && (
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            padding: '12px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #f59e0b'
          }} data-testid="filter-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={18} style={{ color: '#d97706' }} />
              <span style={{ fontWeight: '600', color: '#92400e' }}>
                Showing unpaid proposals only (Draft + Sent)
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setFilterMode(null);
                setSearchParams({});
              }}
              style={{ color: '#92400e' }}
              data-testid="clear-filter-btn"
            >
              <X size={16} /> Clear Filter
            </Button>
          </div>
        )}

        {/* Top Bar with Tabs, Search, Actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {/* Left: Title + Tabs */}
          <div className="dash-title-row" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: '800', 
              margin: 0,
              color: '#000',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Proposals
            </h1>
            <ProposalAllowance refreshKey={proposals.length} />
            
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '8px', padding: '4px' }}>
              {[
                { id: 'open', label: 'Open', value: formatCurrency(openTotal) },
                { id: 'archived', label: 'Archived', value: archivedCount > 0 ? `(${archivedCount})` : null },
                { id: 'lost', label: 'Lost' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: activeTab === tab.id ? 'white' : 'transparent',
                    color: activeTab === tab.id ? '#111827' : '#6b7280',
                    fontWeight: activeTab === tab.id ? '600' : '500',
                    fontSize: '13px',
                    cursor: 'pointer',
                    boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  {tab.label}
                  {tab.value && <span style={{ marginLeft: '6px', color: '#e74c3c' }}>{tab.value}</span>}
                </button>
              ))}
            </div>
          </div>
          
          {/* Right: Search, Sort, Filter, Create */}
          <div className="dash-toolbar" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '36px', width: '200px' }}
              />
            </div>
            
            <Button variant="outline" style={{ fontSize: '13px' }}>
              SORT
            </Button>
            
            <Button variant="outline" style={{ fontSize: '13px' }}>
              <Filter size={14} /> FILTER
            </Button>
            
            <Button 
              onClick={() => navigate('/quote')}
              style={{ background: '#e74c3c', fontSize: '13px' }}
            >
              <Plus size={16} /> CREATE
            </Button>
          </div>
        </div>

        {/* Kanban Board */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>Loading proposals...</div>
        ) : activeTab === 'archived' ? (
          <div data-testid="archived-list" style={{ background: 'white', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, color: '#374151' }}>
              Archived Proposals ({filterBySearch(proposals).length})
            </h3>
            {filterBySearch(proposals).length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                Nothing archived yet. Use the ⋮ menu on any proposal to archive it.
              </p>
            ) : (
              filterBySearch(proposals).map(p => <ProposalCard key={p.id} proposal={p} />)
            )}
          </div>
        ) : activeTab === 'lost' ? (
          <div style={{ background: 'white', borderRadius: 8, padding: 32, textAlign: 'center', color: '#9ca3af' }}>
            Lost proposals view — coming soon.
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            gap: '24px',
            overflowX: 'auto',
            paddingBottom: '16px'
          }}>
            <KanbanColumn 
              title="Draft" 
              count={draftProposals.length}
              total={draftTotal}
              proposals={filterBySearch(draftProposals)}
              color="#f59e0b"
            />
            <KanbanColumn 
              title="Sent" 
              count={sentProposals.length}
              total={sentTotal}
              proposals={filterBySearch(sentProposals)}
              color="#3b82f6"
            />
            {/* Approved / In Production / Ready for Pickup — always rendered so admins can drag
                the FIRST card into them. Empty columns show a subtle "No proposals" hint. */}
            <KanbanColumn
              title="Approved"
              count={approvedProposals.length}
              total={approvedTotal}
              proposals={filterBySearch(approvedProposals)}
              color="#22c55e"
            />
            <KanbanColumn
              title="In Production"
              count={inProductionProposals.length}
              total={inProductionTotal}
              proposals={filterBySearch(inProductionProposals)}
              color="#8b5cf6"
            />
            <KanbanColumn
              title="Ready for Pickup"
              count={readyForPickupProposals.length}
              total={readyForPickupTotal}
              proposals={filterBySearch(readyForPickupProposals)}
              color="#14b8a6"
            />
            {/* Hide Paid column when filtering for unpaid */}
            {filterMode !== 'unpaid' && (
              <KanbanColumn 
                title="Paid" 
                count={paidProposals.length}
                total={paidTotal}
                proposals={filterBySearch(paidProposals)}
                color="#10b981"
              />
            )}
            {/* Outstanding balances — partial payments where customer still owes.
                Highlights edit-after-payment cases that would otherwise slip through. */}
            {outstandingProposals.length > 0 && (
              <KanbanColumn
                title="Outstanding Balances"
                count={outstandingProposals.length}
                total={outstandingTotal}
                totalLabel="balance to collect"
                proposals={filterBySearch(outstandingProposals)}
                color="#c2410c"
                cardComponent={BalanceCard}
              />
            )}
          </div>
        )}
      </main>

      {/* ===== KANBAN DRAG-TO-STATUS CONFIRMATION MODAL ===== */}
      {dndConfirm && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}
          onClick={() => !dndSaving && setDndConfirm(null)}
          data-testid="dnd-confirm-modal"
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 460, borderRadius: 10, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>
                Move to {dndConfirm.targetLabel}?
              </div>
              <button onClick={() => setDndConfirm(null)} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}>
                <X size={18} color="#64748B" />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, marginBottom: 14 }}>
                {dndConfirm.hint}
              </div>
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: 14, fontSize: 13, color: '#475569' }}>
                <div style={{ fontWeight: 600, color: '#0F172A', marginBottom: 2 }}>{dndConfirm.proposal.customer_name}</div>
                <div style={{ color: '#0F172A' }}>Auto-email will be sent to <strong>{dndConfirm.proposal.customer_email}</strong></div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button
                  onClick={() => applyStatusChangeFromDnD(dndConfirm.proposal.id, dndConfirm.targetStatus, false)}
                  disabled={dndSaving}
                  data-testid="dnd-skip-email-btn"
                  style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 500 }}
                >
                  Skip Email
                </button>
                <button
                  onClick={() => applyStatusChangeFromDnD(dndConfirm.proposal.id, dndConfirm.targetStatus, true)}
                  disabled={dndSaving}
                  data-testid="dnd-send-email-btn"
                  style={{ flex: 1.4, padding: '10px 14px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {dndSaving ? 'Saving…' : (<><Send size={14} /> Move & Send Email</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
